import { LanguageService } from '..';
import { EventItem, MediaItem } from '../../components/view/search-list/types';
import { retry } from '../../utils/retry';
import {
  EventsSearchParams,
  LocationsSearchParams,
  NewsSearchParams,
  NewsYearsSearchParams,
  StockInformationSearchParams,
  AdhocSearchParams,
  EventYearSearchParams,
  PubMedSearchParams,
  PatientSearchParams,
} from './types';
import { PipelineEntry } from '../filter';

const rootDomain = process.env.GATSBY_EXTERNAL_DATA_API_URL;
const storyblokRootDomain = process.env.GATSBY_STORYBLOK_DATA_API_URL;

const buildEndpointMap = (root: string, storyblokRoot: string) => ({
  rootDomain: root,
  endpoint: {
    adhocAnnouncement: `${root}/news/adhoc`,
    adhocAnnouncementYears: `${root}/news/adhoc/years`,
    diagnostics: `${root}/pipeline-diagnostics`,
    events: `${root}/events`,
    eventYears: `${root}/events/years`,
    locations: `${root}/locations`,
    locationsByContinent: `${root}/locations/adresses`,
    news: `${root}/news`,
    newsYears: `${root}/news/years`,
    patient: `${root}/patient-organisations`,
    patientCountries: `${root}/patient-organisations/countries`,
    pipeline: `${root}/pipeline`,
    pipelineChanges: `${root}/pipeline-changes`,
    stockInformation: `${root}/stock-information`,
    pubMed: `${root}/pubmed`,
    pubMedCategories: `${root}/pubmed/categories`,
    safetyOfficers: `${root}/local-safety-officers`,
    safetyReporting: `${root}/local-safety-reportings`,
    upcomingEvents: `${root}/events/upcoming`,
    datasourceEntries: `${storyblokRoot}/datasource_entries`,
    stories: `${storyblokRoot}/stories`,
  },
});

export interface DefaultEndpoint<T> {
  Total: number;
  Page: number;
  Items: T;
}

export const ExternalDataService = {
  ...buildEndpointMap(rootDomain, storyblokRootDomain),
  async get(endpoint: RequestInfo, params?: RequestInit): Promise<unknown> {
    const apiResponse = await fetch(endpoint, params)
      .then((response) => response.json())
      // eslint-disable-next-line no-console
      .catch((error) => console.error(error));
    return apiResponse || {};
  },
  buildSearchParams(params: Record<string, unknown>): string {
    return Object.entries(params)
      .flatMap((array) => `${array[0]}=${array[1]}`)
      .join('&');
  },
  getLocalisedValue(object: Record<string, unknown>, key: string, locale: string): string {
    return object[key]?.[locale] || object[key]?.[LanguageService.getDefaultLocale()];
  },
  async getEventYears(params: EventYearSearchParams): Promise<string[]> {
    return this.get(`${this.endpoint.eventYears}?${this.buildSearchParams(params)}`);
  },
  async getEvents(params: EventsSearchParams): Promise<DefaultEndpoint<MediaItem[]>> {
    return this.get(`${this.endpoint.events}?${this.buildSearchParams(params)}`);
  },
  async getUpcomingEvents(params: EventsSearchParams): Promise<DefaultEndpoint<EventItem[]>> {
    return this.get(`${this.endpoint.upcomingEvents}?${this.buildSearchParams(params)}`);
  },
  async getLocationsByContinent(params: LocationsSearchParams): Promise<unknown> {
    return this.get(`${this.endpoint.locations}?${this.buildSearchParams(params)}`);
  },
  async getNewsYears(params: NewsYearsSearchParams): Promise<string[]> {
    return this.get(`${this.endpoint.newsYears}?${this.buildSearchParams(params)}`);
  },
  async getNews(params: NewsSearchParams): Promise<DefaultEndpoint<MediaItem[]>> {
    return this.get(`${this.endpoint.news}?${this.buildSearchParams(params)}`);
  },
  async getAdhoc(params?: AdhocSearchParams): Promise<DefaultEndpoint<MediaItem[]>> {
    return this.get(`${this.endpoint.adhocAnnouncement}?${this.buildSearchParams(params)}`);
  },
  async getAdhocYears(): Promise<string[]> {
    return this.get(`${this.endpoint.adhocAnnouncementYears}`);
  },
  async getSafetyReporting(): Promise<unknown> {
    return this.get(this.endpoint.safetyReporting);
  },
  async getSafetyOfficers(): Promise<unknown> {
    return this.get(this.endpoint.safetyOfficers);
  },
  async getStockInformation(params: StockInformationSearchParams): Promise<unknown> {
    return this.get(`${this.endpoint.stockInformation}?${this.buildSearchParams(params)}`);
  },
  async getLocation(key: string): Promise<unknown> {
    return this.get(`${this.rootDomain}/locations/${key}`);
  },
  async getAllCountryLocationsByContinent(params: LocationsSearchParams): Promise<unknown> {
    return this.get(`${this.endpoint.locationsByContinent}?${this.buildSearchParams(params)}`);
  },
  async getPipeline(): Promise<{ Items: PipelineEntry[] }> {
    return this.get(this.endpoint.pipeline);
  },
  async getPipelineDiagnostics(): Promise<unknown> {
    return this.get(this.endpoint.diagnostics);
  },
  async getPipelineChanges(): Promise<unknown> {
    return this.get(this.endpoint.pipelineChanges);
  },
  async getPubMed(params: PubMedSearchParams, searchQuery: string, category = '') {
    return this.get(
      `${this.endpoint.pubMed}?${this.buildSearchParams(params)}&query=${encodeURIComponent(
        searchQuery,
      )}&function=${encodeURIComponent(category)}`,
    );
  },
  async getPubMedCategories(): Promise<{ functions: string[] }> {
    return this.get(this.endpoint.pubMedCategories);
  },
  async getPatients(params: PatientSearchParams): Promise<unknown> {
    return this.get(`${this.endpoint.patient}?${this.buildSearchParams(params)}`);
  },
  async getDatasourceEntries(datasource: string,
    page: number = 1, itemsPerPage: number = 100): Promise<unknown[]> {
    const params = {
      token: process.env.GATSBY_STORYBLOK_SPACE_API_KEY,
      datasource,
      page,
      per_page: itemsPerPage,
    };

    const queryString = this.buildSearchParams(params);
    const response = await this.get(`${this.endpoint.datasourceEntries}?${queryString}`);

    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { datasource_entries } = response;

    if (itemsPerPage * page < datasource_entries.length) {
      const nextPageEntries = await this.getDatasourceEntries(
        datasource,
        page + 1,
        itemsPerPage);
      return [...datasource_entries, ...nextPageEntries];
    }

    return datasource_entries;
  },
  getContentStyleOverwrite(): string {
    return `
      <style>
        #gnw_attachments_section-header,#gnw_attachments_section-items{display:none;}
      </style>
      `;
  },

  async handle404(response) {
    // TODO: Update API and use response status code for handling 404
    if (response.message || response.error) {
      window.location.replace('/404');
    }
  },

  async getWithRetry(endpoint: RequestInfo | URL,
    maxRetries: number, minRetryIntervalMs: number): Promise<unknown> {
    return retry(async () => {
      const httpResult = await this.get(endpoint);
      return httpResult;
    }, maxRetries, minRetryIntervalMs);
  },
};
